import { Camelized, camelizeKeys, decamelizeKeys } from 'humps';
import useWebSocket, { Options } from 'react-use-websocket';
import config from '@/config';
import { useRef } from 'react';

type WsOptions<T> = Omit<Options, 'onMessage'> & {
  onMessage?(event: MessageEvent<T>): void;
};

export default function useWs<
  Data extends object,
  Body extends object = object,
>(route: string, options: WsOptions<Data> = {}) {
  const attemptsRef = useRef(0);
  const reconnectAttempts = options.reconnectAttempts ?? 5;

  const ws = useWebSocket<Data>(`${config.BASE_WS_URL}/${route}`, {
    reconnectAttempts,
    ...options,
    onReconnectStop() {
      attemptsRef.current = 0;
    },
    shouldReconnect,
    share: true,
    onMessage,
  });

  function onMessage(event: MessageEvent<any>) {
    const eventData: string = event.data;
    const data: Camelized<Data> = camelizeKeys<Data>(JSON.parse(eventData));
    options.onMessage?.({
      ...event,
      data: data as Data,
    });
  }

  function shouldReconnect(event: CloseEvent) {
    if (attemptsRef.current >= reconnectAttempts) {
      return false;
    }
    attemptsRef.current++;
    return options.shouldReconnect?.(event) ?? !event.reason;
  }

  const sendJsonMessage = <JsonMessage = Body>(message: JsonMessage) => {
    ws.sendJsonMessage(decamelizeKeys(message));
  };
  const lastJsonMessage: Camelized<Data> = camelizeKeys<Data>(
    ws.lastJsonMessage,
  );

  return { ...ws, sendJsonMessage, lastJsonMessage };
}
